import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { Page, Reveal, MaskedLine } from "@/components/Page";
import { useLang } from "@/i18n";
import { useContent } from "@/content";
import { useSeo } from "@/hooks/useSeo";
import { fileUrl } from "@/api";

const localized = (value, lang, fallback = "") => {
  if (value == null) return fallback;

  if (typeof value === "string" || typeof value === "number") {
    return value;
  }

  if (typeof value === "object") {
    return value[lang] ?? value.tr ?? value.en ?? fallback;
  }

  return fallback;
};

const galleryBlocks = (gallery) => {
  const g = Array.isArray(gallery) ? gallery.filter(Boolean) : [];
  const blocks = [];
  let i = 0;

  if (g.length) {
    blocks.push({ type: "full", src: g[0] });
    i = 1;
  }

  while (i < g.length) {
    if (i + 1 < g.length) {
      blocks.push({
        type: "duo",
        left: g[i],
        right: g[i + 1],
      });
      i += 2;
    } else {
      blocks.push({
        type: "full",
        src: g[i],
      });
      i += 1;
    }
  }

  return blocks;
};

export default function ProjectDetail() {
  const { id } = useParams();
  const reduced = useReducedMotion();
  const { lang, t } = useLang();
  const cms = useContent();

  const list = Array.isArray(cms.projects)
    ? cms.projects
    : [];

  const idx = list.findIndex((p) => p?.id === id);
  const project = idx >= 0 ? list[idx] : null;

  const next =
    list.length && idx >= 0
      ? list[(idx + 1) % list.length]
      : null;

  const projectTitle = localized(
    project?.title,
    lang,
    ""
  );

  const projectDescription = localized(
    project?.description,
    lang,
    ""
  );

  const projectCategory = localized(
    project?.category,
    lang,
    ""
  );

  const projectLocation = localized(
    project?.location,
    lang,
    ""
  );

  const projectStatus = localized(
    project?.status,
    lang,
    ""
  );

  const projectCredits = localized(
    project?.credits,
    lang,
    ""
  );

  const seoTitle = localized(
    project?.seo?.title,
    lang,
    projectTitle ? `${projectTitle} — SOLIDOR` : undefined
  );

  const seoDescription = localized(
    project?.seo?.description,
    lang,
    projectDescription || undefined
  );

  useSeo({
    title: project ? seoTitle : undefined,
    description: project ? seoDescription : undefined,
    image: project?.cover
      ? fileUrl(project.cover)
      : undefined,
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  if (!project) {
    return (
      <Page
        testid="project-not-found"
        className="min-h-screen flex items-center justify-center bg-bone"
      >
        <p className="font-mono text-xs uppercase tracking-[0.3em] text-clay">
          {t.project.notFound}
        </p>
      </Page>
    );
  }

  const META = [
    {
      k: t.project.location,
      v: projectLocation,
    },
    {
      k: t.project.year,
      v: localized(project.year, lang, ""),
    },
    {
      k: t.project.type,
      v: projectCategory,
    },
    {
      k: t.project.status,
      v: projectStatus,
    },
  ];

  const gallery = galleryBlocks(project.gallery);

  const nextTitle = localized(
    next?.title,
    lang,
    ""
  );

  return (
    <Page
      testid={`project-detail-${project.id}`}
      className="bg-bone"
    >
      <section className="relative h-[88svh] overflow-hidden bg-ink">
        <motion.img
          src={fileUrl(project.cover)}
          alt={projectTitle}
          className="h-full w-full object-cover"
          initial={
            reduced
              ? false
              : { scale: 1.1 }
          }
          animate={{ scale: 1 }}
          transition={{
            duration: 1.8,
            ease: [0.22, 1, 0.36, 1],
          }}
        />

        <div className="absolute inset-0 bg-ink/30" />

        <div className="absolute inset-x-0 bottom-0 px-5 sm:px-10 pb-14 text-white">
          <span className="font-mono text-[10px] uppercase tracking-[0.35em] text-white/70">
            <MaskedLine delay={0.4}>
              {projectCategory}
            </MaskedLine>
          </span>

          <h1 className="mt-3 text-2xl sm:text-4xl lg:text-5xl font-light uppercase tracking-tight leading-[0.98]">
            <MaskedLine delay={0.55}>
              {projectTitle}
            </MaskedLine>
          </h1>
        </div>
      </section>

      <section className="px-5 sm:px-10 py-16 sm:py-24">
        <Reveal>
          <dl
            data-testid="project-metadata-grid"
            className="grid grid-cols-2 md:grid-cols-4 gap-8 border-y border-ink/10 py-8"
          >
            {META.map((m, i) => (
              <div key={`${m.k}-${i}`}>
                <dt className="font-mono text-[10px] uppercase tracking-[0.3em] text-clay">
                  {m.k}
                </dt>

                <dd className="mt-2 text-sm font-light">
                  {m.v}
                </dd>
              </div>
            ))}
          </dl>
        </Reveal>

        <Reveal className="mt-16 sm:mt-24 max-w-2xl">
          <p className="text-base sm:text-lg lg:text-xl font-light leading-relaxed text-ink/90">
            {projectDescription}
          </p>

          {projectCredits && (
            <p className="mt-6 font-mono text-[10px] uppercase tracking-[0.25em] text-clay">
              {projectCredits}
            </p>
          )}
        </Reveal>
      </section>

      <section
        data-testid="project-gallery"
        className="px-5 sm:px-10 pb-24 sm:pb-32 flex flex-col gap-10 sm:gap-16"
      >
        {gallery.map((block, i) =>
          block.type === "full" ? (
            <Reveal key={i}>
              <div className="overflow-hidden bg-stone">
                <img
                  src={fileUrl(block.src)}
                  alt={`${projectTitle} — view ${i + 1}`}
                  loading="lazy"
                  className="w-full h-[60vh] sm:h-[85vh] object-cover"
                />
              </div>
            </Reveal>
          ) : (
            <div
              key={i}
              className="grid grid-cols-1 sm:grid-cols-2 gap-10 sm:gap-16"
            >
              <Reveal>
                <div className="overflow-hidden bg-stone">
                  <img
                    src={fileUrl(block.left)}
                    alt={`${projectTitle} — detail`}
                    loading="lazy"
                    className="w-full aspect-[3/4] object-cover"
                  />
                </div>
              </Reveal>

              <Reveal className="sm:mt-24">
                <div className="overflow-hidden bg-stone">
                  <img
                    src={fileUrl(block.right)}
                    alt={`${projectTitle} — detail`}
                    loading="lazy"
                    className="w-full aspect-[3/4] object-cover"
                  />
                </div>
              </Reveal>
            </div>
          )
        )}
      </section>

      {next && (
        <Link
          to={`/projects/${next.id}`}
          data-testid="next-project-link"
          className="group relative block h-[55vh] overflow-hidden bg-ink"
        >
          <img
            src={fileUrl(next.cover)}
            alt={nextTitle}
            loading="lazy"
            className="h-full w-full object-cover opacity-60 transition-all duration-[1200ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:opacity-80 group-hover:scale-[1.03]"
          />

          <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 text-white">
            <span className="font-mono text-[10px] uppercase tracking-[0.35em] text-white/70">
              {t.project.next}
            </span>

            <span className="text-2xl sm:text-4xl font-light uppercase tracking-tight">
              {nextTitle}
            </span>

            <span
              aria-hidden
              className="text-xl transition-transform duration-500 group-hover:translate-x-3"
            >
              →
            </span>
          </div>
        </Link>
      )}
    </Page>
  );
}
